export type TextReplacementValue = string | number;
export type TextReplacements = Record<string, TextReplacementValue>;
export type TextTranslations = Record<string, string>;

const PLACEHOLDER_PATTERN = /\{([A-Za-z_$][\w$.-]*)\}/g;

export function interpolateText(
  template: string,
  replacements?: TextReplacements,
): string {
  if (!replacements) return template;
  return template.replace(PLACEHOLDER_PATTERN, (placeholder, name: string) => {
    if (!Object.prototype.hasOwnProperty.call(replacements, name)) {
      return placeholder;
    }
    return String(replacements[name]);
  });
}

export function findTranslatedTemplate(
  sourceText: string,
  translations: TextTranslations,
): string | undefined {
  if (Object.prototype.hasOwnProperty.call(translations, sourceText)) {
    return translations[sourceText];
  }
  const trimmed = sourceText.trim();
  if (
    trimmed &&
    trimmed !== sourceText &&
    Object.prototype.hasOwnProperty.call(translations, trimmed)
  ) {
    return translations[trimmed];
  }
  return undefined;
}

export function hasSourceTranslation(
  sourceText: string,
  translations: TextTranslations,
): boolean {
  return findTranslatedTemplate(sourceText, translations) !== undefined;
}

/** Templates that still have no translation; later `requestI18n` sends only these. */
export function untranslatedSourceTexts(
  sourceTexts: readonly string[],
  translations: TextTranslations,
): string[] {
  return sourceTexts.filter((text) => !hasSourceTranslation(text, translations));
}

export function hasMissingSourceTranslations(
  sourceTexts: readonly string[],
  translations: TextTranslations,
): boolean {
  return untranslatedSourceTexts(sourceTexts, translations).length > 0;
}

export function sourceTextsSignature(sourceTexts: readonly string[]): string {
  return [...sourceTexts].sort().join('\0');
}

/** After the first host reply, only request again for templates still missing. */
export function shouldRequestI18nForSourceTexts(
  sourceTexts: readonly string[],
  translations: TextTranslations,
  state: {
    i18nNeeded: boolean;
    lastRequestedSourceTexts: string;
  },
): boolean {
  if (!state.i18nNeeded) return false;
  const missing = untranslatedSourceTexts(sourceTexts, translations);
  if (missing.length === 0) return false;
  return sourceTextsSignature(missing) !== state.lastRequestedSourceTexts;
}

export function shouldHoldForMissingTranslation(
  sourceText: string,
  translations: TextTranslations,
  state: {
    sourceLocale: string;
    targetLocale: string;
    i18nNeeded: boolean;
    firstI18nSettled: boolean;
    resolvedSourceTexts: ReadonlySet<string>;
  },
): boolean {
  if (!state.sourceLocale) return false;
  const trimmed = sourceText.trim();
  if (!trimmed) return false;
  if (hasSourceTranslation(sourceText, translations)) return false;
  // Flush mounts Text before the first requestI18n. targetLocale still equals
  // sourceLocale and i18nNeeded is still false, so holding only after settle
  // would paint the source copy, then swap it when the host replies.
  if (!state.firstI18nSettled) return true;
  if (!state.i18nNeeded) return false;
  if (state.sourceLocale.toLowerCase() === state.targetLocale.toLowerCase()) return false;
  return !state.resolvedSourceTexts.has(trimmed);
}

export function localizeText(
  sourceText: string,
  replacements: TextReplacements | undefined,
  sourceLocale: string,
  targetLocale: string,
  translations: TextTranslations,
): string {
  const shouldTranslate =
    !!sourceLocale &&
    !!targetLocale &&
    sourceLocale.toLowerCase() !== targetLocale.toLowerCase();
  const template = shouldTranslate
    ? findTranslatedTemplate(sourceText, translations) ?? sourceText
    : sourceText;
  return interpolateText(template, replacements);
}

export function addSourceTexts(
  target: Set<string>,
  texts?: Iterable<string | undefined | null>,
) {
  if (!texts) return;
  for (const text of texts) {
    const source = typeof text === 'string' ? text.trim() : '';
    if (source) target.add(source);
  }
}

export function mergeTranslations(
  current: TextTranslations,
  incoming: Record<string, unknown>,
): TextTranslations {
  const next = { ...current };
  for (const [key, value] of Object.entries(incoming)) {
    if (typeof value === 'string') next[key] = value;
  }
  return next;
}
